import { Children, isValidElement, useRef, type ReactElement, type ReactNode } from 'react';
import clsx from 'clsx';
import { Strong } from '@clay-run/glaze/text';
import { useScrollFade } from './useScrollFade';

export type ModuleProps = {
  children: ReactNode;
  className?: string;
  /** A module that meets the bottom edge of the viewport squares its bottom corners. */
  meetsViewport?: boolean;
  /** The header holds a 28px control — top padding drops from 16 to 12. */
  headerControl?: boolean;
  /** Renders the bottom scroll fade once the content actually overflows. @default true */
  scrollFade?: boolean;
  /**
   * Whether the body scrolls internally. @default true. `false` when the
   * module's child owns its own scroll corridor (e.g. `PeopleTable`) — else
   * the module would double-scroll (plan 005 §0.13).
   */
  bodyScroll?: boolean;
  /**
   * The body clips on the x axis instead of scrolling. `overflow-y: auto` makes the x axis
   * `auto` too, so a child that cancels the module's `px-4` with `-mx-4` (a tab body that
   * pads itself) sticks 16px past the scroller's right edge and the whole body scrolls
   * sideways. Wide content must scroll inside its own wrapper. @default false
   */
  clipX?: boolean;
  /**
   * The module's background fill. `'primary'` (default) is the standard
   * white module on the rail's gray canvas. `'secondary'` is gray — for a
   * module that itself sits on a white rail body (e.g. inside a Sheet),
   * where a `'primary'` fill would be invisible against its own backdrop.
   * @default 'primary'
   */
  fill?: 'primary' | 'secondary';
  'data-testid'?: string;
};

/**
 * Port of PROTO `src/ui/Module.tsx`. A `Module.Header` child, if present, is
 * pulled out and pinned above the scroll region — the title stays put while
 * the rest of the module's content scrolls beneath it.
 */
export function Module({
  children,
  className,
  meetsViewport = false,
  headerControl = false,
  scrollFade = true,
  bodyScroll = true,
  clipX = false,
  fill = 'primary',
  'data-testid': dataTestId,
}: ModuleProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const { showFade } = useScrollFade({ outerRef, innerRef });

  const childArray = Children.toArray(children);
  const headerChild = childArray.find(
    (child): child is ReactElement<ModuleHeaderProps> => isValidElement(child) && child.type === ModuleHeader,
  );
  const bodyChildren = headerChild ? childArray.filter(child => child !== headerChild) : childArray;

  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        '@container relative flex min-h-0 flex-col gap-3 rounded-lg px-4',
        fill === 'secondary' ? 'bg-bg-secondary' : 'bg-bg-primary',
        meetsViewport ? 'rounded-b-none pb-0' : 'rounded-b-lg pb-4',
        headerControl ? 'pt-3' : 'pt-4',
        fill === 'secondary' ? '[&_thead]:bg-bg-secondary' : '[&_thead]:bg-bg-primary',
        className,
      )}
    >
      {headerChild}
      <div ref={outerRef} className={clsx('flex min-h-0 flex-1 flex-col', bodyScroll && 'overflow-y-auto', clipX && 'overflow-x-hidden')}>
        <div ref={innerRef} className="flex flex-col gap-3">
          {bodyChildren}
        </div>
      </div>
      {scrollFade && (
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t to-transparent',
            fill === 'secondary' ? 'from-bg-secondary' : 'from-bg-primary',
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            showFade ? 'opacity-100' : 'opacity-0',
            meetsViewport ? 'rounded-b-none' : 'rounded-b-lg',
          )}
        />
      )}
    </div>
  );
}

export type ModuleHeaderProps = {
  /**
   * Module title — Strong Extra Small, content-primary, one style for every
   * module, unless the header is `plain` (the caller sets its own content) or
   * `scrollable` (a control strip).
   */
  children: ReactNode;
  /** Right-aligned controls (Segmented Button, Button Ghost icon-only …) — HUG, not FILL. */
  controls?: ReactNode;
  /**
   * The header holds a control strip (a Glaze `TabList`) rather than a title.
   * Glaze's tab list neither wraps nor scrolls on its own, and the default
   * title wrapper is `truncate` (`overflow: hidden`) — so at narrow widths the
   * trailing tabs are clipped and unreachable by touch. `scrollable` swaps the
   * `Strong` title wrapper for a plain `div` that scrolls on the x axis instead
   * (bare `overflow-x-auto`, same as `table/LedgerToolbar.tsx`'s strip — no
   * hidden-scrollbar hack; on touch the scrollbar is an overlay anyway).
   * `whitespace-nowrap` is kept explicitly so a tab label's min-content width
   * stays its full single-line width — that is what makes the tabs overflow
   * (and scroll) instead of wrapping to two lines. @default false
   */
  scrollable?: boolean;
  /**
   * The header holds block content of its own (a heading, a description
   * line) rather than a one-line title. The title wrapper is a `Strong`,
   * which may hold only phrasing content, so `plain` swaps it for a plain
   * `div` that neither truncates nor styles its children. @default false
   */
  plain?: boolean;
};

function ModuleHeader({ children, controls, scrollable = false, plain = false }: ModuleHeaderProps) {
  let title: ReactNode;
  if (scrollable) title = <div className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap">{children}</div>;
  else if (plain) title = <div className="min-w-0 flex-1">{children}</div>;
  else title = <Strong className="min-w-0 flex-1 truncate text-xs">{children}</Strong>;
  return (
    <div className="flex shrink-0 items-center justify-between gap-2">
      {title}
      {controls && <div className="flex shrink-0 items-center gap-2">{controls}</div>}
    </div>
  );
}

Module.Header = ModuleHeader;
